import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { WorkspaceSubItem } from "@/lib/workspace-registry";

/**
 * Workspace-local keyboard: Alt+1–9 jumps to submodule; arrows move focus between tab chips.
 */
export function useContentSubtabsKeyboard(
  items: WorkspaceSubItem[],
  enabled: boolean,
  chipSelector = '[data-subtab-chip="true"]',
) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled || !items.length) return;

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isInput =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.tagName === "SELECT");
      if (isInput && !e.altKey) return;

      if (e.altKey && !e.ctrlKey && !e.metaKey && /^[1-9]$/.test(e.key)) {
        const idx = Number(e.key) - 1;
        const item = items[idx];
        if (!item) return;
        e.preventDefault();
        e.stopPropagation();
        void navigate({
          to: item.to as "/",
          search: (item.search ?? undefined) as never,
          params: (item.params ?? undefined) as never,
        });
        return;
      }

      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") {
        return;
      }

      const chips = Array.from(
        document.querySelectorAll<HTMLElement>(chipSelector),
      ).filter((el) => el.offsetParent !== null);
      if (!chips.length) return;

      const activeIdx = chips.findIndex(
        (el) => el.getAttribute("aria-current") === "page" || el === document.activeElement,
      );
      let nextIdx = activeIdx >= 0 ? activeIdx : 0;

      if (e.key === "ArrowLeft") nextIdx = Math.max(0, nextIdx - 1);
      else if (e.key === "ArrowRight") nextIdx = Math.min(chips.length - 1, nextIdx + 1);
      else if (e.key === "Home") nextIdx = 0;
      else if (e.key === "End") nextIdx = chips.length - 1;

      if (nextIdx === activeIdx && e.key !== "Home" && e.key !== "End") return;
      e.preventDefault();
      chips[nextIdx]?.focus();
    }

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, items, navigate, chipSelector]);
}
